<script lang="ts">
import purify from 'dompurify';
import { inject, watch } from 'vue';

import { PhxsgIcon as PhxsgCoreIcon } from '@sg-core/src/View/Components/Atoms/Icon';
import { PhxsgLoader } from '@sg-core/src/View/Components/Atoms/Loader';

export interface PhxsgAutocompleteOptionSecondaryAction {
  icon: string;
  label: string;
  class?: string;
}

export interface PhxsgAutocompleteOption {
  key: string;
  value: string;
  data?: unknown;
  icon?: string;
  divided?: boolean;
  secondaryAction?: PhxsgAutocompleteOptionSecondaryAction;
}

interface Props {
  id: string;
  currentOption: string;
  label?: string;
  options: PhxsgAutocompleteOption[];
  noResultText: string;
  loading: boolean;
  loadingText: string;
  query: string;
}
</script>

<script setup lang="ts">
const { currentOption, id, options, query } = defineProps<Props>();

const emit = defineEmits<{
  'option-select': [option: PhxsgAutocompleteOption];
  'option-action': [option: PhxsgAutocompleteOption];
}>();

const PhxsgIcon = inject('PhxsgIcon', PhxsgCoreIcon);

function highlightText(str: string): string {
  if (query === '') {
    return purify.sanitize(str);
  }

  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');

  return purify.sanitize(str.replace(regex, '<mark>$1</mark>'));
}

function onOptionAction(option: PhxsgAutocompleteOption, e: MouseEvent): void {
  e.preventDefault();
  e.stopPropagation();
  emit('option-action', option);
}

function optionId(index: number): string {
  return `${id}__option--${index}`;
}

watch(
  () => currentOption,
  (newValue) => {
    const currentIndex = options.findIndex(({ key }) => key === newValue);

    document.getElementById(optionId(currentIndex))?.scrollIntoView({ block: 'nearest' });
  },
);
</script>

<template>
  <ul :id="id" role="listbox" :aria-label="label" class="phxsg-autocomplete__options">
    <li
      v-for="(option, index) in options"
      :id="optionId(index)"
      :key="option.key"
      role="option"
      :aria-selected="currentOption === option.key"
      :class="[
        'phxsg-autocomplete__option',
        {
          'phxsg-autocomplete__option--current': currentOption === option.key,
          'phxsg-autocomplete__option--divided': option.divided,
        },
      ]"
      @mousedown.prevent.stop
      @mouseup="emit('option-select', option)"
    >
      <slot
        name="option"
        v-bind="{
          ...option,
          highlightedText: highlightText(option.value),
          highlightedTextFn: highlightText,
          current: currentOption === option.key,
        }"
      >
        <div class="phxsg-autocomplete__option-content">
          <PhxsgIcon v-if="option.icon" :id="option.icon" />

          <span class="phxsg-autocomplete__option-text" v-html="highlightText(option.value)" />

          <button
            v-if="option.secondaryAction"
            :class="['phxsg-btn phxsg-btn--icon phxsg-btn--icon-only', option.secondaryAction.class]"
            :aria-label="option.secondaryAction.label"
            @mouseup="onOptionAction(option, $event)"
          >
            <PhxsgIcon :id="option.secondaryAction.icon" />
          </button>
        </div>
      </slot>
    </li>

    <li v-if="loading" class="phxsg-autocomplete__option phxsg-autocomplete__option--loading">
      <slot name="loading" v-bind="{ loadingText }">
        <PhxsgLoader />
        {{ loadingText }}
      </slot>
    </li>

    <li
      v-if="!loading && options.length === 0 && noResultText"
      class="phxsg-autocomplete__option phxsg-autocomplete__option--empty"
    >
      <slot name="empty" v-bind="{ noResultText }">{{ noResultText }}</slot>
    </li>
  </ul>
</template>
